import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import type { TableOfContentsBlockAttsInterface } from '~/types/AppData/Blocks/TableOfContentsBlockInterface';
import type { ListItemBlockInterface } from '~/types/AppData/Blocks/ListItemBlockInterface';
import type { TitleBlockInterface } from '~/types/AppData/Blocks/TitleBlockInterface';
import type { InlineTextBlockInterface } from '~/types/AppData/Blocks/InlineTextBlockInterface';
import type { BlockType } from '~/types/AppData/Blocks/BlockType';
import type { FAQBlockInterface } from '~/types/AppData/Blocks/FAQBlockInterface';
import { ArticleContext } from '~/contexts/ArticleContext';
import { cva } from 'class-variance-authority';
import { getCSSColorChildLinks } from '~/utilities/getCSS';
import { AppDataContext } from '~/contexts/AppDataContext';

function isInlineTextHeading(block: InlineTextBlockInterface) {
  return block.type === 'inline-text' && block.value.html.startsWith('<h2>');
}

function createHeadingFromInlineTextBlock(
  block: InlineTextBlockInterface,
  index: number
) {
  const regex = /<h2>(.*)<\/h2>/;
  const match = block.value.html.match(regex);
  return { id: `inline-text-${index}`, title: match ? match[1] : '' };
}

function createHeadingFromListItemBlock(
  block: ListItemBlockInterface,
  index: number
) {
  const titleBlock = block.value.items.find(
    (item) => item.type === 'title'
  ) as TitleBlockInterface;
  return { id: `list-item-${index}`, title: titleBlock.value.text };
}

function createHeadingFromFAQBlock(block: FAQBlockInterface, index: number) {
  const titleBlock = block.value.items
    ? block.value.items[0]
    : { value: { text: '' } };
  return { id: `faq-${index}`, title: titleBlock?.value.text || '' };
}

function createTableOfContentsHeadlines(articleBody: BlockType[]) {
  return articleBody
    .map((block, index) => {
      switch (block.type) {
        case 'list-item':
          return createHeadingFromListItemBlock(
            block as ListItemBlockInterface,
            index
          );
        case 'faq':
          return createHeadingFromFAQBlock(block as FAQBlockInterface, index);
        case 'inline-text':
          return isInlineTextHeading(block as InlineTextBlockInterface)
            ? createHeadingFromInlineTextBlock(
                block as InlineTextBlockInterface,
                index
              )
            : null;
        default:
          return null;
      }
    })
    .filter((heading) => !!heading);
}

const listClasses = cva('toc text-primary mt-4 flex flex-col gap-y-2.5', {
  variants: {
    isNumbered: {
      true: 'ps-5',
      false: 'ps-10 list-decimal',
    },
    color: getCSSColorChildLinks(),
  },
});

export default component$(
  ({ title }: TableOfContentsBlockAttsInterface): JSX.Element => {
    const article = useContext(ArticleContext);
    const { themeComponents } = useContext(AppDataContext);

    if (!article) return <></>;

    const toc = createTableOfContentsHeadlines(article.body as BlockType[]);

    const isNumbered = toc[0]?.title.startsWith('1.');

    return (
      <>
        {title && <h2 class='text-primary'>{title}</h2>}
        <ol
          data-testid={`toc-${article.finiteScrollNumber}`}
          class={listClasses({
            isNumbered,
            color: themeComponents.articleBlock.paragraphLinkColor.value,
          })}
        >
          {toc.map((headline) => {
            if (!headline) return null;

            // Use dangerouslySetInnerHTML so that HTML entities are encoded
            return (
              <li key={headline.id}>
                <a
                  href={`#${headline.id}`}
                  dangerouslySetInnerHTML={headline.title}
                />
              </li>
            );
          })}
        </ol>
      </>
    );
  }
);
